<template>
  <div class="cnfltd-search-wrapper">
    <div
      class="cnfltd-search-form"
      :class="[`cd-theme-${theme}`, `cd-layout-${layout}`]"
    >
      <a-form
        :model="formState"
        layout="vertical"
        @submit="handleSubmit"
        data-testid="domain-search-form"
      >
        <!-- Domain Input with optional form title and www. prefix -->
        <div class="cnfltd-input-wrapper">
          <span v-if="showTitle" class="cnfltd-form-title" data-testid="search-form-title">{{ config.title }}</span>
          <div class="cnfltd-input-group">
            <span class="cnfltd-www-prefix">www.</span>
            <a-input
              v-model:value="formState.domainInput"
              :placeholder="placeholder"
              size="large"
              :disabled="isSearching"
              class="cnfltd-domain-input"
              data-testid="domain-search-input"
            />
            <a-button
              type="primary"
              html-type="submit"
              size="large"
              :loading="isSearching"
              class="cnfltd-search-button"
              data-testid="domain-search-button"
            >
              {{ buttonText }}
            </a-button>
          </div>
        </div>
      </a-form>

      <!-- Informational Text -->
      <div v-if="showInfo" class="cnfltd-info-text">
        <InfoCircleOutlined />
        <span>Enter your desired domain name to check availability</span>
      </div>
    </div>

    <!-- TLD Chips Display - Outside gradient background -->
    <div v-if="displayTlds.length > 0" class="cnfltd-tld-chips" data-testid="tld-chips-display">
      <span
        v-for="tld in displayTlds"
        :key="tld.tld"
        class="cnfltd-tld-chip"
        data-testid="tld-chip"
        :data-tld="tld.tld"
      >
        <span class="cnfltd-tld-name">{{ tld.tld }}</span>
        <span class="cnfltd-tld-price">{{ currencySymbol }}{{ formatPrice(tld.price) }}</span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Domain Search Form Component
 *
 * Features:
 * - Configurable form title (show_title parameter)
 * - Title appears on gradient background, separate from white input group
 * - White background only covers www. prefix, input, and button
 * - TLD chips display below with pricing (supports sale pricing)
 * - Theme and layout customization
 * - Bulk search mode (no TLD) vs single domain check (with TLD)
 */
import { reactive, computed } from 'vue'
import { InfoCircleOutlined } from '@ant-design/icons-vue'
import type { ShortcodeConfiguration } from '../types/config'

// Props
interface Props {
  config: ShortcodeConfiguration
  isSearching?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isSearching: false
})

// Emits
const emit = defineEmits<{
  search: [domain: string, tlds: string[]]
}>()

// Form state
const formState = reactive({
  domainInput: ''
})

// Computed properties from config
const placeholder = computed(() => props.config.placeholder)
const buttonText = computed(() => props.config.button_text)
const theme = computed(() => props.config.theme)
const layout = computed(() => props.config.layout)
const showInfo = computed(() => props.config.show_info)
const showTitle = computed(() => props.config.show_title)
const currencySymbol = computed(() => props.config.currency_symbol)

// Get display TLDs with pricing (first 6-8 TLDs)
const displayTlds = computed(() => {
  const maxDisplay = 8
  const tlds = props.config.available_tlds.slice(0, maxDisplay)
  const pricingMap = new Map(
    props.config.tld_pricing.map((p: any) => [p.tld, p])
  )

  return tlds.map((tld: string) => {
    const pricing = pricingMap.get(tld) as any
    if (!pricing) {
      return { tld, price: 0 }
    }

    // Use sale price if on sale, otherwise registration price
    const price = pricing.on_sale && pricing.sale_price
      ? pricing.sale_price
      : pricing.registration_price || 0

    return { tld, price }
  })
})

// Format price with 2 decimal places
const formatPrice = (price: number): string => {
  return price.toFixed(2)
}

// Form submission handler
const handleSubmit = () => {
  if (!formState.domainInput.trim()) {
    return
  }

  const input = formState.domainInput.trim()

  // Parse the input to detect if it includes a TLD
  const parts = input.split('.')

  if (parts.length > 1) {
    // Input contains a dot, likely a full domain (e.g., "example.com")
    const sld = parts[0]
    const tld = '.' + parts.slice(1).join('.')

    // Single domain check mode
    emit('search', sld, [tld])
  } else {
    // No TLD detected - bulk search mode with all TLDs from pricing
    // Use all TLDs from tld_pricing if available, otherwise fallback to available_tlds
    const allTlds = props.config.tld_pricing && props.config.tld_pricing.length > 0
      ? props.config.tld_pricing.map(p => p.tld)
      : props.config.available_tlds

    emit('search', input, allTlds)
  }
}
</script>

<style scoped>
/* Wrapper for entire search component */
.cnfltd-search-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.cnfltd-search-form {
  padding: 2rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 100%;
}

/* Theme variations */
.cnfltd-theme-light {
  background: linear-gradient(135deg, #4a5a7f 0%, #3a4a6f 100%);
  color: #ffffff;
}

.cnfltd-theme-dark {
  background: linear-gradient(135deg, #1f1f1f 0%, #2a2a2a 100%);
  color: #ffffff;
}

/* Input wrapper with optional form title */
.cnfltd-input-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* Form title - on gradient background, separate from white input group */
.cnfltd-form-title {
  padding: 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 400;
  color: #ffffff;
  height: 48px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  text-transform: lowercase;
}

/* Input group - contains www. prefix, input, and button with white background */
.cnfltd-input-group {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 0;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.cnfltd-www-prefix {
  padding: 0 1rem;
  font-size: 1.125rem;
  font-weight: 500;
  color: #666;
  background: #f5f5f5;
  height: 48px;
  display: flex;
  align-items: center;
  border-right: 1px solid #e0e0e0;
}

.cnfltd-domain-input {
  flex: 1;
  border: none !important;
  box-shadow: none !important;
}

.cnfltd-domain-input:focus {
  border: none !important;
  box-shadow: none !important;
}

.cnfltd-search-button {
  border-radius: 0 !important;
  height: 48px;
  padding: 0 2rem;
  background: #a4c639 !important;
  border-color: #a4c639 !important;
  font-weight: 600;
  text-transform: lowercase;
}

.cnfltd-search-button:hover {
  background: #8fb02d !important;
  border-color: #8fb02d !important;
}

/* Informational text */
.cnfltd-info-text {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border-left: 3px solid #a4c639;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #ffffff;
}

/* TLD chips display - centered and outside background */
.cnfltd-tld-chips {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 1.5rem;
  padding: 0 8px;
  width: 100%;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
}

.cnfltd-tld-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 4px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #f0f0f0;
  min-height: 80px;
}

.cnfltd-tld-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.cnfltd-tld-chip .cnfltd-tld-name {
  color: #1a1a1a;
  font-weight: 800;
  font-size: 1rem;
}

.cnfltd-tld-chip .cnfltd-tld-price {
  color: #3b82f6;
  font-weight: 700;
  margin-top: 2px;
  font-size: 0.8rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .cnfltd-search-form {
    padding: 1.5rem;
  }

  .cnfltd-input-wrapper {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .cnfltd-form-title {
    height: auto;
    justify-content: center;
    text-align: center;
  }

  .cnfltd-input-group {
    flex-direction: column;
    background: transparent;
    box-shadow: none;
    gap: 12px;
  }

  .cnfltd-www-prefix {
    display: none; /* Hide on mobile to save space, standard in modern search */
  }

  .cnfltd-domain-input {
    width: 100% !important;
    background: #ffffff !important;
    border-radius: 8px !important;
    height: 64px !important;
    min-height: 64px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    border: 1px solid #d9d9d9 !important;
    display: flex !important;
    align-items: center !important;

    :deep(input.ant-input),
    :deep(.ant-input) {
      height: 64px !important;
      min-height: 64px !important;
      padding: 0 32px !important;
      font-size: 16px !important;
      font-weight: 500 !important;
      border: none !important;
      background: transparent !important;
      line-height: 64px !important;
    }
    
    :deep(.ant-input-suffix) {
      font-size: 18px !important;
      margin-right: 12px !important;
    }
  }

  .cnfltd-search-button {
    width: 100% !important;
    border-radius: 8px !important;
    height: 64px !important;
    min-height: 64px !important;
    font-size: 16px !important;
    font-weight: 600 !important;
    margin-top: 8px !important;
  }
}
</style>
